// 11. 10. 2026 (owner, docs/thepit.md): The Pit, the arena in Calder Steelworks. The Foundry runs the fights and Danny
// Dawn reads the results on the air. From level 10 a survivor signs up (and may leave; back in only after 24 h); only
// those signed up attack and are attacked. It is not a live fight: the attacker fights the defender's saved survivor (its
// arena gear, tree and defense style as they are now), the same fight as PvP, played by itself, under the arena's rules:
// no ambush, no escape (to the fall or 10 rounds, then the bigger share of HP left wins), no consumables, and the perks
// for ambushes and expeditions do nothing. Nothing is lost: no gear, no durability.
//
// Before the fight the attacker picks a style (Aggressive beats Balanced, Balanced beats Defensive, Defensive beats
// Aggressive); the defender's style is set in advance (Balanced at first). The side with the edge deals +7% damage (`styleEdgePct`; 10 before deploy8's measurement) in
// that fight. A rating like Elo (1,000 to start) moves for both sides; leagues by level (10–14, 15–20); three opponents of
// the league to pick from (a bit weaker, about the same, a bit stronger). Five attacks a day: three free, two for 1 energy
// each; defenses are not limited. Rewards: Scrap and the level's materials for an attack won, a little Scrap for a
// defense won, a weekly chest by the place in the league, and at the season's end (the Sweep, which resets the rating)
// the title Pit Champion, a frame and a banner, and the Foundry outfit for the top 1%.
//
// Everything here is pure (the server, the client, the tests and the simulations share it). Behind the server's flag
// FEATURE_PIT (off by default): `setPitOn` switches the rules that the shared code reads (the unlock, the schedule).

import { GAME } from './constants';
import { simulateCombat, type CombatResult, type CombatRound, type Fighter } from './combat';
import { tierForLevel } from './content/items';
import { RUN_INCOME } from './items/craftCost';
import { PERK_EFFECT_GROUPS, type PerkEffect, type PerkEffectType } from './items/perks';
import { SCHEDULE_PARTS } from './schedule';
import { SKILL_NODES, learnedNodes, skillBuildName } from './skills/tree';
import { deriveStats, type SurvivorBuild } from './formulas';
import { generateNpc } from './npc';
import { Rng } from './rng';
import type { BodyType } from './characters';
import type { GearSnapshotEntry } from './gearGlimpse';
import type { OutfitRef } from './outfits';
import type { CombatStats, GearSlot, StackKind, StackMap } from './types';

// ---- The rules -------------------------------------------------------------------------------------------------

export const PIT_RULES = {
  /** The server's FEATURE_PIT: off until the owner turns it on (`setPitOn` at the server's start). */
  on: false,
  /** The level it opens at (the schedule's row and the rule by level). */
  openLevel: 10,
  /** Protection: only an account older than this many days signs up (28. 10. 2026, owner: 5, was 7). */
  minAccountDays: 5,
  /** Back in only this many hours after leaving. */
  rejoinHours: 24,
  startRating: 1000,
  /** No rating goes under this. */
  ratingFloor: 100,
  /** The Elo factor: a win between equals moves both by half of it. */
  eloK: 32,
  /** No escape: the fight goes to the fall or this many rounds, then the bigger share of HP left wins. */
  maxRounds: GAME.MAX_COMBAT_ROUNDS,
  /**
   * The style's edge: +% damage for that fight (the measurement: it moves wins by 5–8 points, not more). 12. 10. 2026
   * (`sim:pit`, deploy8): +10% moved them 10.4 (L12) and 9.6 (L18) points, +7% 7.3 and 6.6, +6% 7.0 and 5.9: 7.
   */
  styleEdgePct: 7,
  attacksPerDay: 5,
  freeAttacks: 3,
  /** Each attack after the free ones. */
  energyPerExtra: 1,
  /** Protection: at most this many fights between the same two survivors in 24 hours (either way). */
  samePairPer24h: 2,
  /**
   * The three opponents: `near` is about the same rating, `gap` the least step to a weaker or a stronger one, `far` the
   * most; the pick is one of the three closest in each band (the day's dice), so the list changes.
   */
  offerBand: { near: 75, gap: 25, far: 300, pool: 3 },
  /** The offers are kept this long (or until an attack); a new list on request after `refreshMinutes`. */
  offerTtlMinutes: 60,
  refreshMinutes: 10,
  /** Scouting: the defender's last defenses, the style shown on the newest ones. */
  scoutDefenses: 5,
  scoutStyles: 3,
  /**
   * Rewards in shares of what a 60 minute run brings on the survivor's tier (`RUN_INCOME`): an attack won pays Scrap and
   * the level's two materials, a defense won a little Scrap (at most `defenseRewardsPerDay` defenses a day pay).
   */
  winRuns: 0.3,
  defenseRuns: 0.1,
  defenseRewardsPerDay: 10,
  /** The weekly table ranks those with at least this many attacks in the week; the season's end those with this many in the season. */
  weekMinAttacks: 3,
  seasonMinAttacks: 10,
  /** A win over a rating at least this much higher is an upset (its share card). */
  upsetGap: 150,
  /** A suspicious pair: the same winner over the same loser this many times within `days`, never the other way. */
  suspicious: { fights: 4, days: 7 },
  /** Fights in the history list. */
  history: 20,
};

/** 11. 10. 2026: the server's FEATURE_PIT switches the shared rules too (the unlock at level 10, the schedule's popup). */
export function setPitOn(on: boolean) {
  PIT_RULES.on = on;
  SCHEDULE_PARTS.pit = on;
}

// ---- Leagues ---------------------------------------------------------------------------------------------------

export interface PitLeague {
  id: 1 | 2;
  name: string;
  min: number;
  max: number;
  /** Its emblem (`ART`, folder `pit`). */
  art: string;
}

/** By level; a new league comes when the level cap goes up. */
export const PIT_LEAGUES: PitLeague[] = [
  { id: 1, name: 'Slag League', min: 10, max: 14, art: 'pit_league_1' },
  { id: 2, name: 'Steel League', min: 15, max: GAME.LEVEL_CAP, art: 'pit_league_2' },
];

/** The league of a level (below the first: the first; above the last: the last). */
export function pitLeagueOf(level: number): PitLeague {
  return PIT_LEAGUES.find((l) => level >= l.min && level <= l.max) ?? (level < PIT_LEAGUES[0].min ? PIT_LEAGUES[0] : PIT_LEAGUES[PIT_LEAGUES.length - 1]);
}

export const pitLeague = (id: number): PitLeague => PIT_LEAGUES.find((l) => l.id === id) ?? PIT_LEAGUES[0];

// ---- Styles ----------------------------------------------------------------------------------------------------

export const PIT_STYLES = ['aggressive', 'balanced', 'defensive'] as const;
export type PitStyle = (typeof PIT_STYLES)[number];

export const PIT_STYLE_DEF: Record<PitStyle, { name: string; beats: PitStyle; art: string }> = {
  aggressive: { name: 'Aggressive', beats: 'balanced', art: 'pit_style_aggressive' },
  balanced: { name: 'Balanced', beats: 'defensive', art: 'pit_style_balanced' },
  defensive: { name: 'Defensive', beats: 'aggressive', art: 'pit_style_defensive' },
};

export const isPitStyle = (x: unknown): x is PitStyle => typeof x === 'string' && (PIT_STYLES as readonly string[]).includes(x);

/** The side with the edge: `a` the attacker, `b` the defender, null for the same style. */
export function styleEdge(att: PitStyle, def: PitStyle): 'a' | 'b' | null {
  if (att === def) return null;
  return PIT_STYLE_DEF[att].beats === def ? 'a' : 'b';
}

/** The style that beats this one. */
export const styleAgainst = (s: PitStyle): PitStyle => PIT_STYLES.find((x) => PIT_STYLE_DEF[x].beats === s)!;

// ---- Rating ----------------------------------------------------------------------------------------------------

/** The chance Elo gives side a against side b. */
export function eloExpected(a: number, b: number): number {
  return 1 / (1 + Math.pow(10, (b - a) / 400));
}

/** The attacker's change (the defender's is the opposite): a win is at least +1, a loss at least −1. */
export function eloChange(att: number, def: number, attWon: boolean, k = PIT_RULES.eloK): { att: number; def: number } {
  let d = Math.round(k * ((attWon ? 1 : 0) - eloExpected(att, def)));
  if (attWon && d < 1) d = 1;
  if (!attWon && d > -1) d = -1;
  return { att: d, def: -d };
}

/** A rating after a change, never under the floor. */
export const applyRating = (rating: number, delta: number) => Math.max(PIT_RULES.ratingFloor, Math.round(rating + delta));

// ---- Attacks a day ---------------------------------------------------------------------------------------------

/** What the next attack costs in energy after `used` today: 0 for the free ones, null when the day's are gone. */
export function pitAttackCost(used: number): number | null {
  if (used >= PIT_RULES.attacksPerDay) return null;
  return used < PIT_RULES.freeAttacks ? 0 : PIT_RULES.energyPerExtra;
}

// ---- Opponents -------------------------------------------------------------------------------------------------

export type PitOfferKind = 'weaker' | 'similar' | 'stronger';

export interface PitCandidate {
  id: string;
  rating: number;
}

/** What an offer is, by the rating's difference. */
export function offerKind(mine: number, theirs: number): PitOfferKind {
  const d = theirs - mine;
  if (d < -PIT_RULES.offerBand.gap) return 'weaker';
  if (d > PIT_RULES.offerBand.gap) return 'stronger';
  return 'similar';
}

/**
 * The three opponents (owner: one a bit weaker, one about the same, one a bit stronger): first the one about the same
 * (one of the `pool` closest within `near`), then a weaker one below it and a stronger one above it (one of the closest in
 * their bands), so the three are always in that order and keep their slot's name. A band with nobody in it takes the
 * closest one left (named by its rating). Fewer candidates: fewer offers. Weakest first.
 */
export function pickOffers<T extends PitCandidate>(mine: number, list: readonly T[], rng: Rng): { kind: PitOfferKind; c: T }[] {
  const { near, gap, far, pool } = PIT_RULES.offerBand;
  const used = new Set<string>();
  const take = (cands: T[]): T | null => {
    const free = cands.filter((c) => !used.has(c.id)).slice(0, pool);
    if (!free.length) return null;
    const c = free.length === 1 ? free[0] : free[Math.floor(rng.next() * free.length)];
    used.add(c.id);
    return c;
  };
  const dist = (c: T) => Math.abs(c.rating - mine);
  const similar = take(list.filter((c) => dist(c) <= near).sort((a, b) => dist(a) - dist(b)));
  const low = Math.min(mine - gap, similar ? similar.rating : Infinity);
  const high = Math.max(mine + gap, similar ? similar.rating : -Infinity);
  const weaker = take(list.filter((c) => c.rating < low && c.rating >= mine - far).sort((a, b) => b.rating - a.rating));
  const stronger = take(list.filter((c) => c.rating > high && c.rating <= mine + far).sort((a, b) => a.rating - b.rating));
  const out: { kind: PitOfferKind; c: T }[] = [];
  if (weaker) out.push({ kind: 'weaker', c: weaker });
  if (similar) out.push({ kind: 'similar', c: similar });
  if (stronger) out.push({ kind: 'stronger', c: stronger });
  // Empty bands: the closest left.
  const rest = list.filter((c) => !used.has(c.id)).sort((a, b) => dist(a) - dist(b));
  while (out.length < 3 && rest.length) {
    const c = rest.shift()!;
    out.push({ kind: offerKind(mine, c.rating), c });
  }
  return out.sort((a, b) => a.c.rating - b.c.rating);
}

// ---- Rewards ---------------------------------------------------------------------------------------------------

/** The level's two materials (by the tier's runs: what its gear asks for most besides Scrap). */
export const PIT_LEVEL_MATERIALS: Record<1 | 2 | 3 | 4, [StackKind, StackKind]> = {
  1: ['metal', 'cloth'],
  2: ['metal', 'electronics'],
  3: ['metal', 'chemicals'],
  4: ['electronics', 'chemicals'],
};

const runShare = (level: number, share: number, mats: boolean): StackMap => {
  const tier = tierForLevel(level);
  const inc = RUN_INCOME[tier];
  const out: StackMap = { scrap: Math.max(1, Math.round((inc.scrap ?? 0) * share)) };
  if (mats) for (const m of PIT_LEVEL_MATERIALS[tier]) out[m] = Math.max(1, Math.round((inc[m] ?? 0) * share));
  return out;
};

/** An attack won (each of the day's five can pay): Scrap and the level's materials. */
export const pitWinReward = (level: number): StackMap => runShare(level, PIT_RULES.winRuns, true);

/** A defense won: a little Scrap. */
export const pitDefenseReward = (level: number): StackMap => runShare(level, PIT_RULES.defenseRuns, false);

export type PitChestTier = 'top10' | 'top25' | 'top50';

/**
 * The weekly chests by the place in the league (owner: top 10% with 2 Relic Shards since 13. 10. 2026, 3 before, top 25% and top 50%: materials and
 * consumables). `runs`: Scrap and the level's materials worth that many runs of 60 minutes.
 */
export const PIT_CHESTS: Record<PitChestTier, { name: string; share: number; runs: number; consumables: StackMap; shards: number }> = {
  // 13. 10. 2026 (owner, PIT=1: the top 10% +10.5% / +8.3% Relic Shards from the arena, the bound +10%): 2 Shards, not 3.
  top10: { name: "Foundry's chest", share: 0.1, runs: 3, consumables: { medkit: 2, repair_kit: 2 }, shards: 2 },
  top25: { name: "Fighter's chest", share: 0.25, runs: 2, consumables: { medkit: 1, bandage: 3, repair_kit: 1 }, shards: 0 },
  top50: { name: 'Pit chest', share: 0.5, runs: 1, consumables: { bandage: 3, ration: 2 }, shards: 0 },
};

export const PIT_CHEST_TIERS: PitChestTier[] = ['top10', 'top25', 'top50'];

/** The last place each chest reaches among `ranked` (at least the first place for the top 10%). */
export function pitChestCuts(ranked: number): Record<PitChestTier, number> {
  const cut = (share: number) => (ranked > 0 ? Math.max(1, Math.ceil(ranked * share)) : 0);
  return { top10: cut(PIT_CHESTS.top10.share), top25: cut(PIT_CHESTS.top25.share), top50: cut(PIT_CHESTS.top50.share) };
}

/** The chest of a place (1 = first) among `ranked`, or null. */
export function pitChestTier(place: number | null, ranked: number): PitChestTier | null {
  if (!place || place < 1 || place > ranked) return null;
  const cuts = pitChestCuts(ranked);
  return PIT_CHEST_TIERS.find((t) => place <= cuts[t]) ?? null;
}

/** What a chest holds on a level. */
export function pitChestStacks(tier: PitChestTier, level: number): StackMap {
  const c = PIT_CHESTS[tier];
  const out = runShare(level, c.runs, true);
  for (const [k, v] of Object.entries(c.consumables) as [StackKind, number][]) out[k] = (out[k] ?? 0) + v;
  if (c.shards) out.relic_shard = c.shards;
  return out;
}

// ---- Foundry fighters ------------------------------------------------------------------------------------------

/**
 * 11. 10. 2026 (owner's decision): when the league has fewer than three real opponents for a survivor, the Foundry puts
 * in its own fighters for the empty places: a generated survivor (gear, tree, attributes and style, as a real defender's)
 * on the attacker's level and about its Power (a bit under, the same, a bit over, by the place it takes). Real players
 * always come first. A win against one pays half the reward and half the rating change (the attacker's only: nobody
 * defends). They are never in the weekly table. A fight against one uses one of the day's attacks and counts toward the
 * week's attack minimum for the chest (the owner's clarification), never toward the season's titles.
 */
export const PIT_FOUNDRY = {
  rewardShare: 0.5,
  ratingShare: 0.5,
  idPrefix: 'foundry_',
  /** Their rating: the attacker's, a step under or over by the place. */
  ratingStep: 80,
  /** Their Power: the attacker's arena Power, this share under or over by the place. */
  powerStep: 0.08,
  /** Generated survivors tried; the closest Power is evened out with attribute points. */
  tries: 12,
  names: ['Rivet Kane', 'Slag Morrow', 'Ingot Pike', 'Clinker Dahl', 'Ladle Jones', 'Cinder Voss', 'Billet Shaw', 'Anvil Mae', 'Crucible Tom', 'Tapper Rook', 'Smelt Ivy', 'Bellows Ruiz'],
};

export const isFoundryId = (id: string) => id.startsWith(PIT_FOUNDRY.idPrefix);

/** What makes a Foundry fighter again (kept with the offers, so the fight is the one scouted). */
export interface PitFoundrySeed {
  seed: string;
  level: number;
  /** The Power it is made for. */
  power: number;
  rating: number;
  kind: PitOfferKind;
}

export interface PitFoundryFighter {
  id: string;
  name: string;
  level: number;
  build: SurvivorBuild;
  stats: CombatStats;
  style: PitStyle;
  rating: number;
  kind: PitOfferKind;
}

const KIND_SIGN: Record<PitOfferKind, number> = { weaker: -1, similar: 0, stronger: 1 };

/** The seed of a Foundry fighter for a place, from the attacker's rating, level and arena Power. */
export function foundrySeed(seed: string, kind: PitOfferKind, mine: { rating: number; level: number; power: number }): PitFoundrySeed {
  const s = KIND_SIGN[kind];
  return { seed, level: mine.level, power: Math.round(mine.power * (1 + s * PIT_FOUNDRY.powerStep)), rating: mine.rating + s * PIT_FOUNDRY.ratingStep, kind };
}

/** The pieces a Foundry fighter takes off first when it is too strong for the place (never the weapon). */
const STRIP_ORDER: GearSlot[] = ['accessory', 'gloves', 'helmet', 'pants', 'boots', 'body', 'backpack'];

/**
 * The fighter of a seed (the same every time): the generated survivor whose Power is closest to the target; too strong,
 * it takes off pieces (accessory first, never the weapon) while still over; then a point at a time on its best attribute
 * and VIT until it is within 3% (at most 150 points, never under 5).
 */
export function foundryFighter(id: string, f: PitFoundrySeed): PitFoundryFighter {
  const rng = new Rng(`foundry:${f.seed}`);
  let best: { build: SurvivorBuild; power: number } | null = null;
  for (let i = 0; i < PIT_FOUNDRY.tries; i++) {
    const build = generateNpc(f.level, rng.fork(`npc${i}`)).build;
    const power = deriveStats(build).power;
    if (!best || Math.abs(power - f.power) < Math.abs(best.power - f.power)) best = { build, power };
  }
  let build = best!.build;
  let power = best!.power;
  const over = () => power > f.power * 1.03;
  for (const slot of STRIP_ORDER) {
    if (!over()) break;
    if (!build.equipment[slot]) continue;
    const { [slot]: _off, ...rest } = build.equipment;
    build = { ...build, equipment: rest };
    power = deriveStats(build).power;
  }
  const top = (['str', 'per', 'agi'] as const).reduce((a, k) => (build.attrs[k] > build.attrs[a] ? k : a), 'str' as 'str' | 'per' | 'agi');
  for (let i = 0; i < 150 && Math.abs(power - f.power) > f.power * 0.03; i++) {
    const d = power < f.power ? 1 : -1;
    // Down: the bigger of the two first, so neither hits the floor early.
    const k = d < 0 ? (build.attrs.vit > build.attrs[top] ? 'vit' : top) : i % 2 ? 'vit' : top;
    if (build.attrs[k] + d < 5) break;
    build = { ...build, attrs: { ...build.attrs, [k]: build.attrs[k] + d } };
    power = deriveStats(build).power;
  }
  const stats = deriveStats(build);
  const pick = rng.fork('look');
  return { id, name: pick.pick(PIT_FOUNDRY.names), level: f.level, build, stats, style: pick.pick(PIT_STYLES), rating: f.rating, kind: f.kind };
}

/** The places Foundry fighters take: the kinds the real opponents left empty (about the same first), up to three in all. */
export function foundryKinds(real: readonly { kind: PitOfferKind }[]): PitOfferKind[] {
  const need = Math.max(0, 3 - real.length);
  const free = (['similar', 'weaker', 'stronger'] as PitOfferKind[]).filter((k) => !real.some((r) => r.kind === k));
  const out: PitOfferKind[] = [];
  for (let i = 0; i < need; i++) out.push(free[i] ?? 'similar');
  return out;
}

/** A win against a Foundry fighter: half the reward (each stack, at least 1). */
export function pitFoundryReward(level: number): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(pitWinReward(level)) as [StackKind, number][]) out[k] = Math.max(1, Math.round(v * PIT_FOUNDRY.rewardShare));
  return out;
}

/** The attacker's rating change against a Foundry fighter: half of Elo's (a win at least +1, a loss at least −1). */
export function foundryRatingChange(att: number, def: number, won: boolean): number {
  const d = Math.round(eloChange(att, def, won).att * PIT_FOUNDRY.ratingShare);
  return won ? Math.max(1, d) : Math.min(-1, d);
}

// ---- The season's end (the Sweep) ------------------------------------------------------------------------------

/**
 * Owner: "Pit Champion" for the first place of every league, a frame and a banner for the top 10, the Foundry outfit for
 * the top 1% (at least the first place). 11. 10. 2026 [default]: the outfit in Epic, when the items package brings the
 * Foundry set (`foundry` in the outfits); until then it waits on the account and is given once it exists.
 */
export const PIT_SEASON = {
  title: 'Pit Champion',
  championAchievement: 'pit_champion',
  /** The banner: a badge next to the name (the achievement's). */
  bannerAchievement: 'pit_top10',
  /** The share card frame. */
  frame: 'pit_top10',
  top: 10,
  outfitShare: 0.01,
  outfit: { set: 'foundry', rarity: 'epic' } as { set: string; rarity: 'epic' },
  outfitLabel: 'Pit top 1%',
};

export function pitSeasonAwards(place: number | null, ranked: number): { champion: boolean; top10: boolean; outfit: boolean } {
  if (!place || place < 1 || place > ranked) return { champion: false, top10: false, outfit: false };
  return { champion: place === 1, top10: place <= PIT_SEASON.top, outfit: place <= Math.max(1, Math.ceil(ranked * PIT_SEASON.outfitShare)) };
}

// ---- The arena set ---------------------------------------------------------------------------------------------

/** The arena's own gear (owner): item ids by slot, from the survivor's items (the same item can be worn out there too). */
export type PitGear = Partial<Record<GearSlot, string>>;

/**
 * Whether an item is in the arena set (the marketplace never lists one; the item's card says it). Pure: the server
 * passes the survivor's set (`pit_fighters.gear`).
 */
export function isInArenaSet(itemId: string, gear: PitGear | null | undefined): boolean {
  return !!gear && Object.values(gear).includes(itemId);
}

// ---- What works in the Pit -------------------------------------------------------------------------------------

/** The plain stat effects that count in a fight (the rest of the stat group is loot, carrying, crafting, travel…). */
const COMBAT_STATS = new Set<PerkEffectType>(['accuracyPct', 'critPct', 'dodgePct', 'defensePct', 'damagePct', 'damageTakenPct', 'statFlat', 'initiativePct', 'statPct']);

/** Fight effects that need an ambush, an NPC, a run or a consumable (none of them in the Pit). */
const COMBAT_OFF = new Set<PerkEffectType>([
  'ambushDamagePct',
  'ambushFirstHitDamagePct',
  'shadowAmbushAlwaysCrit',
  'npcFleeAtStartChance',
  'enemyEscapePct',
  'autoMedkitInFightBelowHpPct',
  'damagePerKillPct',
  'zoneDamagePct',
  'noAmmoVsNpc',
  'noFuelUse',
  'weaponNoDurabilityLoss',
]);

/** Targets a survivor can match in the Pit (`armored`: 30+ armor). */
const SURVIVOR_TARGETS = new Set(['armored']);

/**
 * Whether a perk's effect works in the Pit: a fight effect or a combat stat, with no zone, open PvP or stance condition,
 * and a target a survivor can be. Ambush and expedition perks (and everything about loot, travel or encounters) do not.
 */
export function pitEffectWorks(e: Pick<PerkEffect, 'type' | 'zones' | 'openPvpOnly' | 'stance' | 'targets'>): boolean {
  const combat = (PERK_EFFECT_GROUPS.combat as readonly string[]).includes(e.type);
  if (!(combat ? !COMBAT_OFF.has(e.type) : COMBAT_STATS.has(e.type))) return false;
  if (e.zones?.length || e.openPvpOnly || e.stance) return false;
  if (e.targets?.length && !e.targets.some((t) => SURVIVOR_TARGETS.has(t))) return false;
  return true;
}

/** A perk does nothing in the Pit when none of its effects work there. */
export const pitPerkOff = (effects: readonly PerkEffect[]) => effects.length > 0 && !effects.some(pitEffectWorks);

/** The fight effects a fighter keeps in the Pit. */
export function pitPerkFx<T extends PerkEffect>(fx: readonly T[] | undefined): T[] {
  return (fx ?? []).filter(pitEffectWorks);
}

// ---- The fight -------------------------------------------------------------------------------------------------

/** One side of a fight: the saved survivor's numbers (its arena set, tree and attributes). */
export interface PitFightSide {
  id: string;
  name: string;
  level: number;
  stats: CombatStats;
}

/**
 * A fighter under the arena's rules: full HP, never runs (no escape), nothing in the pack (no Medkit, no Molotov, no
 * Ammo counted: a firearm shoots at full strength and uses none), no stance, no kills; only the perks that work here.
 * `edge`: the style's damage (`PIT_RULES.styleEdgePct`, +7%).
 */
export function pitFighter(side: PitFightSide, key: 'a' | 'b', edge: boolean): Fighter {
  const stats: CombatStats = { ...side.stats, perkFx: pitPerkFx(side.stats.perkFx), attack: edge ? side.stats.attack * (1 + PIT_RULES.styleEdgePct / 100) : side.stats.attack };
  return { id: key, name: side.name, type: 'survivor', level: side.level, hp: stats.hpMax, hpMax: stats.hpMax, stats, retreatAt: 0, carried: 0, capacity: stats.capacity, canFlee: false, medkits: 0, kills: 0 };
}

export interface PitFightResult {
  combat: CombatResult;
  edge: 'a' | 'b' | null;
  moments: PitMoment[];
}

/** The fight: the attacker (a) against the defender's saved survivor (b), no ambush, to the fall or the last round. */
export function simulatePitFight(att: PitFightSide, def: PitFightSide, attStyle: PitStyle, defStyle: PitStyle, rng: Rng): PitFightResult {
  const edge = styleEdge(attStyle, defStyle);
  const combat = simulateCombat(pitFighter(att, 'a', edge === 'a'), pitFighter(def, 'b', edge === 'b'), rng, { maxRounds: PIT_RULES.maxRounds });
  return { combat, edge, moments: pitMoments(combat, edge) };
}

// ---- The replay's key moments ----------------------------------------------------------------------------------

/**
 * Owner: the fight is shown round by round with its key moments marked: a comeback, the last blow on little HP, the
 * perk that decided it (a finishing crit, a burn, spikes, a second hit, a stun), the style's edge.
 */
export type PitMoment =
  | { kind: 'edge'; round: 0; by: 'a' | 'b' }
  | { kind: 'comeback'; round: number; by: 'a' | 'b'; low: number }
  | { kind: 'lastBlow'; round: number; by: 'a' | 'b'; hpPct: number }
  | { kind: 'perk'; round: number; by: 'a' | 'b'; perk: 'execute' | 'burn' | 'reflect' | 'extra' | 'stun' }
  | { kind: 'decision'; round: number; by: 'a' | 'b'; a: number; b: number };

/** HP of both sides after every round (from the log: a hit's `hpLeft`, spikes, burns). */
export function pitHpByRound(c: CombatResult): { n: number; a: number; b: number }[] {
  const hp = { a: c.a.hpStart, b: c.b.hpStart };
  const out: { n: number; a: number; b: number }[] = [];
  for (const r of c.rounds) {
    for (const b of r.burns ?? []) hp[b.on] = Math.max(0, hp[b.on] - b.dmg);
    for (const h of r.hits) {
      const target = h.by === 'a' ? 'b' : 'a';
      hp[target] = h.hpLeft;
      if (h.reflected) hp[h.by] = Math.max(0, hp[h.by] - h.reflected);
    }
    out.push({ n: r.n, a: hp.a, b: hp.b });
  }
  if (out.length) out[out.length - 1] = { ...out[out.length - 1], a: c.a.hpEnd, b: c.b.hpEnd };
  return out;
}

const pctOf = (hp: number, max: number) => Math.round((Math.max(0, hp) / Math.max(1, max)) * 100);

export function pitMoments(c: CombatResult, edge: 'a' | 'b' | null): PitMoment[] {
  const out: PitMoment[] = [];
  const w = c.winner;
  const l = w === 'a' ? 'b' : 'a';
  const max = { a: c.a.hpStart, b: c.b.hpStart };
  if (edge) out.push({ kind: 'edge', round: 0, by: edge });
  // A comeback: the winner was 25 points or more behind at the end of a round.
  const rows = pitHpByRound(c);
  let worst: { n: number; gap: number; low: number } | null = null;
  for (const r of rows) {
    const gap = pctOf(r[l], max[l]) - pctOf(r[w], max[w]);
    if (gap >= 25 && (!worst || gap > worst.gap)) worst = { n: r.n, gap, low: pctOf(r[w], max[w]) };
  }
  if (worst) out.push({ kind: 'comeback', round: worst.n, by: w, low: worst.low });
  const last = c.rounds[c.rounds.length - 1];
  if (c.end === 'ko' && last) {
    const hpPct = pctOf(c[w].hpEnd, max[w]);
    if (hpPct <= 20) out.push({ kind: 'lastBlow', round: last.n, by: w, hpPct });
    // What finished it: the last thing that took the loser down.
    const hits = last.hits;
    const fin = [...hits].reverse().find((h) => (h.by === w && h.hpLeft <= 0 && h.hit) || (h.by === l && h.reflected));
    let perk: Extract<PitMoment, { kind: 'perk' }>['perk'] | null = null;
    if (fin?.by === l && fin.reflected) perk = 'reflect';
    else if (fin?.executed) perk = 'execute';
    else if (fin?.extra) perk = 'extra';
    else if (!fin && last.burns?.some((b) => b.on === l)) perk = 'burn';
    else if (hits.some((h) => h.by === l && h.stunned)) perk = 'stun';
    if (perk) out.push({ kind: 'perk', round: last.n, by: w, perk });
  } else if (c.end === 'rounds') out.push({ kind: 'decision', round: last?.n ?? PIT_RULES.maxRounds, by: w, a: pctOf(c.a.hpEnd, max.a), b: pctOf(c.b.hpEnd, max.b) });
  return out;
}

// ---- Protection ------------------------------------------------------------------------------------------------

/** A fight between two survivors as the protection reads it (newest last or in any order). */
export interface PitPairFight {
  at: number;
  winner: string;
  loser: string;
}

/**
 * A suspicious pair (owner: always the same winner over the same loser): the same one won at least `fights` times within
 * `days` and never lost to the other in that time. Returns the winner and loser, or null.
 */
export function suspiciousPair(fights: readonly PitPairFight[], now: number): { winner: string; loser: string; n: number } | null {
  const from = now - PIT_RULES.suspicious.days * 86_400_000;
  const recent = fights.filter((f) => f.at >= from);
  if (recent.length < PIT_RULES.suspicious.fights) return null;
  const winners = new Set(recent.map((f) => f.winner));
  if (winners.size !== 1) return null;
  const f = recent[0];
  return { winner: f.winner, loser: f.loser, n: recent.length };
}

/** Why a survivor cannot sign up now (null: it can). */
export function pitJoinBlocker(o: { level: number; accountCreatedAt: number; leftAt: number | null; now: number }): { reason: string; at: number | null } | null {
  if (o.level < PIT_RULES.openLevel) return { reason: `The Pit opens at level ${PIT_RULES.openLevel}.`, at: null };
  const old = o.accountCreatedAt + PIT_RULES.minAccountDays * 86_400_000;
  if (o.now < old) return { reason: `Your account needs ${PIT_RULES.minAccountDays} days in the game before the Pit takes you.`, at: old };
  if (o.leftAt != null && o.now < o.leftAt + PIT_RULES.rejoinHours * 3_600_000) return { reason: `You left the Pit. You can sign up again ${PIT_RULES.rejoinHours} hours after.`, at: o.leftAt + PIT_RULES.rejoinHours * 3_600_000 };
  return null;
}

// ---- What the screens show -------------------------------------------------------------------------------------

/** The tree as scouting shows it: the build's name, the keystone and the notable nodes. */
export interface PitTreeView {
  build: string;
  label: string;
  keystone: string | null;
  notables: string[];
  points: number;
}

export function pitTreeView(skills: Record<string, number> | null | undefined): PitTreeView {
  const learned = learnedNodes(skills);
  const b = skillBuildName(learned);
  const ks = learned.find((id) => SKILL_NODES[id]?.type === 'keystone');
  return { build: b.name, label: b.label, keystone: ks ? SKILL_NODES[ks].name : null, notables: learned.filter((id) => SKILL_NODES[id]?.type === 'notable').map((id) => SKILL_NODES[id].name), points: learned.length };
}

/** A survivor in the Pit, as lists and the replay show it. */
export interface PitFighterView {
  survivorId: string;
  name: string;
  level: number;
  power: number;
  rating: number;
  bodyType: BodyType;
  outfit: OutfitRef | null;
  title: string | null;
  /** The crew's tag next to the name (the crews package); null without one. */
  crewTag: string | null;
  /** 11. 10. 2026 (owner's decision): a Foundry fighter (the Foundry's own, not a player). */
  foundry?: boolean;
}

export interface PitItemView {
  id: string;
  slot: GearSlot;
  templateId: string;
  rarity: string;
  name: string;
  power: number;
  upgrade: number;
  /** The names of its perks that do nothing in the Pit. */
  offPerks: string[];
}

/** Scouting before an attack (owner): the arena set, the tree, the rating and the last five defenses, the style on the newest three. */
export interface PitScoutDTO {
  gear: GearSnapshotEntry[];
  tree: PitTreeView;
  defenses: { at: number; won: boolean; style: PitStyle | null }[];
  /** Perks of the arena set that do nothing here. */
  offPerks: string[];
}

export interface PitOfferDTO extends PitFighterView {
  kind: PitOfferKind;
  /** The rating a win brings, and a loss costs (negative). */
  gain: number;
  loss: number;
  /** What a win pays (for the attacker's level). */
  reward: StackMap;
  scout: PitScoutDTO;
}

export interface PitFightRowDTO {
  id: string;
  at: number;
  role: 'attack' | 'defense';
  opp: PitFighterView;
  myStyle: PitStyle;
  oppStyle: PitStyle;
  won: boolean;
  delta: number;
  edge: 'me' | 'opp' | null;
  reward: StackMap | null;
  /** The replay was opened. */
  seen: boolean;
  upset: boolean;
}

export interface PitReplayDTO {
  id: string;
  at: number;
  /** The attacker (a) and the defender (b), with the styles and max HP of the fight. */
  a: PitFighterView & { style: PitStyle; hpMax: number };
  b: PitFighterView & { style: PitStyle; hpMax: number };
  /** Which side the viewer is (null: neither). */
  me: 'a' | 'b' | null;
  edge: 'a' | 'b' | null;
  winner: 'a' | 'b';
  end: 'ko' | 'rounds' | 'escape';
  rounds: CombatRound[];
  hpEnd: { a: number; b: number };
  moments: PitMoment[];
  delta: { a: number; b: number };
  /** What the viewer got from it. */
  reward: StackMap | null;
  upset: boolean;
  /** The share card's code (an upset won by the viewer). */
  share: string | null;
}

export interface PitLeagueRow {
  place: number | null;
  survivorId: string;
  name: string;
  level: number;
  rating: number;
  crewTag: string | null;
  attacks: number;
  me: boolean;
}

export interface PitWeekDTO {
  week: string;
  endsAt: number;
  league: PitLeague;
  /** Ranked first (by rating), then the rest of the league (fewer attacks this week). */
  rows: PitLeagueRow[];
  ranked: number;
  cuts: Record<PitChestTier, number>;
  myPlace: number | null;
  myTier: PitChestTier | null;
  /** Attacks still needed this week to be ranked. */
  needAttacks: number;
}

export interface PitDTO {
  on: boolean;
  level: number;
  joined: boolean;
  join: { ok: boolean; reason: string | null; at: number | null };
  me: (PitFighterView & { league: PitLeague; style: PitStyle; wins: number; losses: number; defWins: number; defLosses: number; weekAttacks: number }) | null;
  attacks: { used: number; max: number; free: number; nextCost: number | null; energy: number };
  offers: PitOfferDTO[];
  /** When a new list can be asked for (null: now). */
  refreshAt: number | null;
  gear: { slots: { slot: GearSlot; item: PitItemView | null }[]; power: number } | null;
  history: PitFightRowDTO[];
  week: PitWeekDTO | null;
  lastWeek: { week: string; place: number; of: number; tier: PitChestTier | null; league: string } | null;
}

/** The rules as the screen and the Guide say them (numbers from `PIT_RULES`). */
export function pitRulesFacts() {
  return {
    level: PIT_RULES.openLevel,
    days: PIT_RULES.minAccountDays,
    rejoin: PIT_RULES.rejoinHours,
    rounds: PIT_RULES.maxRounds,
    edge: PIT_RULES.styleEdgePct,
    attacks: PIT_RULES.attacksPerDay,
    free: PIT_RULES.freeAttacks,
    extra: PIT_RULES.attacksPerDay - PIT_RULES.freeAttacks,
    energy: PIT_RULES.energyPerExtra,
    pair: PIT_RULES.samePairPer24h,
    start: PIT_RULES.startRating,
    weekMin: PIT_RULES.weekMinAttacks,
    seasonMin: PIT_RULES.seasonMinAttacks,
    top10: Math.round(PIT_CHESTS.top10.share * 100),
    top25: Math.round(PIT_CHESTS.top25.share * 100),
    top50: Math.round(PIT_CHESTS.top50.share * 100),
    shards: PIT_CHESTS.top10.shards,
    top: PIT_SEASON.top,
    outfit: Math.round(PIT_SEASON.outfitShare * 100),
    foundryShare: Math.round(PIT_FOUNDRY.rewardShare * 100),
  };
}

/** Danny Dawn's line about the Pit (the morning broadcast): last night's upset, else the leagues' leaders. */
export interface PitRadioFacts {
  upset: { winner: string; loser: string; gap: number } | null;
  leaders: { league: string; name: string }[];
}

export function pitRadioNews(f: PitRadioFacts | null | undefined): string | null {
  if (!f) return null;
  if (f.upset) return `Down in the Pit, ${f.upset.winner} took down ${f.upset.loser}, ranked ${f.upset.gap} above! The Foundry is still paying out the bets!`;
  if (f.leaders.length) return `The Pit's leaders this morning: ${f.leaders.map((l) => `${l.name} in the ${l.league}`).join(', ')}! Fight night never ends at the Steelworks!`;
  return null;
}
